<template src="./templates/App.html" />

<script setup>

import {
    ref, onMounted, watch, computed,
} from 'vue';
import { useHead } from 'unhead';
import {
    regionFilters,
    categoryFilters,
    taxonomyFilters,
    pageSeoData,
} from './config/config';
import { getBlogBaseURI, getCategoryParamFromURL, getUtmParams } from '../../utils/blogUtils';

import SearchBox from './components/SearchBox.vue';
import FilterHeader from './components/FilterHeader.vue';
import FilterPopup from './components/FilterPopup.vue';
import ResultsCount from './components/ResultsCount.vue';
import GridView from './components/GridView.vue';
import Pagination from './components/Pagination.vue';

const blogTitleDom = document.getElementById('blog-title');
const originalBlogTitle = blogTitleDom ? blogTitleDom.dataset.title : '';
const blogTaglineDom = document.getElementById('blog-tagline');

// Data Properties
const isLoading = ref(true);
const searchQuery = ref('');
const selectedCategory = ref('');
const selectedRegion = ref('');
const selectedTerms = ref({});
const siteMode = ref('');
const results = ref([]);
const currentPage = ref(1);
const totalPages = ref(0);
const totalResults = ref(0);

const regionInfo = {};

const categoriesFacets = categoryFilters;
const regionsFacets = regionFilters;
const hasFilterPopup = taxonomyFilters.length > 0;

// eslint-disable-next-line
onMounted(async () => {

    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.has('stage')) siteMode.value = urlParams.get('stage');
    if (urlParams.has('query')) searchQuery.value = urlParams.get('query');
    const categoryParam = getCategoryParamFromURL();
    if (categoryParam) selectedCategory.value = categoryParam;
    if (urlParams.has('region')) selectedRegion.value = urlParams.get('region');
    if (urlParams.has('terms')) {

        const termIDs = urlParams.get('terms').split('-');
        const terms = {};
        termIDs.forEach((termID) => {

            // eslint-disable-next-line
            termID = parseInt(termID);
            // eslint-disable-next-line
            const type = taxonomyFilters.find((type) => type.terms.find((t) => t.id === termID));
            if (type) {

                terms[type.id] = termID;

            }

        });
        selectedTerms.value = terms;

    }
    if (urlParams.has('page')) currentPage.value = parseInt(urlParams.get('page'), 10);

    // Fetch results based on initial URL parameters
    // eslint-disable-next-line
    fetchResults();

});

const urlSearchParams = computed(() => {

    const params = new URLSearchParams();

    // Add the rest of the query parameters from the application state
    if (siteMode.value) params.set('stage', siteMode.value);
    if (searchQuery.value) params.set('query', searchQuery.value);
    if (selectedRegion.value) params.set('region', selectedRegion.value);
    if (currentPage.value > 1) params.set('page', currentPage.value);

    const termIDs = Object.values(selectedTerms.value);
    if (termIDs.length) {

        params.set('terms', termIDs.join('-'));

    }

    return params;

});

// eslint-disable-next-line
watch(
    [currentPage, selectedCategory, selectedRegion, selectedTerms, searchQuery], (
        [newCurrentPage, newSelectedCategory, newSelectedRegion, newSelectedTerms, newSearchQuery],
        [oldCurrentPage, oldSelectedCategory, oldSelectedRegion, oldSelectedTerms, oldSearchQuery],
    ) => {

        // reset page number if search query changes
        if (newCurrentPage === oldCurrentPage) {

            currentPage.value = 1;

        }
        // eslint-disable-next-line
        fetchResults();

    }, { deep: true });

const clearFilters = () => {

    searchQuery.value = '';
    selectedCategory.value = '';
    selectedRegion.value = '';
    selectedTerms.value = {};

};

const updateBlogTitleAndSEO = () => {

    let seoData = pageSeoData;
    if (selectedCategory.value) {

        const category = categoriesFacets.find((c) => c.category === selectedCategory.value);
        if (blogTitleDom && category) {

            blogTitleDom.innerHTML = category.title;

            if (blogTaglineDom) {

                blogTaglineDom.innerHTML = originalBlogTitle;
                blogTaglineDom.classList.remove('hidden', 'invisible');

            }

        }
        seoData = category.seo;

    } else if (blogTitleDom) {

        blogTitleDom.innerHTML = originalBlogTitle;

        if (blogTaglineDom) {

            blogTaglineDom.innerHTML = '';
            blogTaglineDom.classList.add('hidden', 'invisible');

        }

    }

    useHead(seoData);

};

// Fetch Results
// eslint-disable-next-line
const fetchResults = async () => {

    isLoading.value = true;

    // Construct the new URL
    let baseUrl = getBlogBaseURI();
    const categoryParam = selectedCategory.value ? `category/${selectedCategory.value}/` : '';
    let queryParams = urlSearchParams.value.toString() ? `?${urlSearchParams.value.toString()}` : '';
    const utmParams = getUtmParams();
    if (utmParams.length) {

        const utmString = utmParams.map((param) => `${param.key}=${param.value}`).join('&');
        queryParams = queryParams ? `${queryParams}&${utmString}` : `?${utmString}`;

    }
    const newUrl = baseUrl + categoryParam + queryParams;

    updateBlogTitleAndSEO();

    // Update the browser's URL
    window.history.pushState({}, '', newUrl);

    try {

        const filters = {};
        if (siteMode.value) {

            filters.mode = siteMode.value;

        }
        if (searchQuery.value) {

            filters.query = searchQuery.value;

        }
        if (selectedCategory.value) {

            filters.categorySlug = selectedCategory.value;

        }
        if (selectedRegion.value) {

            filters.regionID = selectedRegion.value;

        }
        const selectedTermIDs = Object.values(selectedTerms.value);
        if (selectedTermIDs.length) {

            filters.termIDs = selectedTermIDs;
            filters.terms = JSON.stringify(selectedTerms.value);

        }
        filters.offset = (currentPage.value - 1);

        if (baseUrl.endsWith('/')) {

            baseUrl = baseUrl.slice(0, -1);

        }
        const response = await fetch(`${baseUrl}/filter-posts/`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(filters),
        });
        const data = await response.json();
        results.value = data.hits;
        totalPages.value = data.pageCount;
        totalResults.value = data.resultsCount;
        isLoading.value = false;

    } catch (error) {

        // eslint-disable-next-line
        console.error('Error fetching results:', error);

    }

};

const updateTaxonomyFilters = (typeID, termID) => {

    selectedTerms.value[typeID] = termID;

};

</script>
